import { del, get, patch, post } from './http';
import type { PointsLevelInfo } from './points';

export type MemberTier = 'member' | 'vip';
export type MemberStatus = 'active' | 'inactive' | 'banned' | 'deleted';

export interface AdminMemberItem {
  id: number;
  name: string;
  email: string;
  daohao: string | null;
  membershipTier: MemberTier;
  accountStatus: MemberStatus;
  namePublic: boolean;
  company: string | null;
  position: string | null;
  phone: string | null;
  avatarUrl: string | null;
  points: number;
  level: PointsLevelInfo;
  isTemporarilyLocked: boolean;
  lockRemainingSeconds: number;
  failedLoginAttempts: number;
  lockedIdentifiers?: string[];
  createdAt: string | null;
  lastLoginAt: string | null;
  deletedAt: string | null;
}

export interface AdminMemberListResult {
  list: AdminMemberItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListAdminMembersParams {
  keyword?: string;
  tier?: MemberTier;
  status?: 'all' | MemberStatus;
  page?: number;
  pageSize?: number;
  sortBy?: 'createdAt' | 'lastLoginAt' | 'points' | 'name' | 'email';
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
}

export interface MemberLedgerItem {
  id: number;
  actionKey: string;
  actionLabel: string;
  pointsDelta: number;
  targetType: string;
  targetTitle: string | null;
  remark: string | null;
  manualBy: string | null;
  createdAt: string | null;
}

export function listAdminMembers(params?: ListAdminMembersParams) {
  return get<AdminMemberListResult>('/admin/members', params ? { params } : undefined);
}

export function getAdminMemberDetail(id: number | string) {
  return get<AdminMemberItem>(`/admin/members/${id}`);
}

export function getAdminMemberLedger(id: number | string, limit = 50) {
  return get<MemberLedgerItem[]>(`/admin/members/${id}/ledger`, { params: { limit } });
}

export function updateAdminMemberStatus(id: number | string, accountStatus: Exclude<MemberStatus, 'deleted'>) {
  return patch<{ message: string }>(`/admin/members/${id}/status`, { accountStatus });
}

export function updateAdminMemberTier(id: number | string, membershipTier: MemberTier) {
  return patch<{ message: string }>(`/admin/members/${id}/tier`, { membershipTier });
}

export function updateAdminMemberProfile(id: number | string, payload: {
  name?: string;
  daohao?: string | null;
  namePublic?: boolean;
  company?: string | null;
  position?: string | null;
  phone?: string | null;
}) {
  return patch<{ message: string }>(`/admin/members/${id}/profile`, payload);
}

export function resetAdminMemberPassword(
  id: number | string,
  payload: { newPassword: string; unlockAfterReset?: boolean },
) {
  return post<{ message: string }>(`/admin/members/${id}/reset-password`, payload);
}

export function unlockAdminMemberLogin(id: number | string) {
  return post<{ cleared: boolean; removedKeys: number }>(`/admin/members/${id}/unlock-login`);
}

export function softDeleteAdminMember(id: number | string) {
  return del<{ message: string }>(`/admin/members/${id}`);
}

export function restoreAdminMember(id: number | string) {
  return post<{ message: string }>(`/admin/members/${id}/restore`);
}

export function permanentlyDeleteAdminMember(id: number | string) {
  return del<{ message: string }>(`/admin/members/${id}/permanent`);
}
